import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert"
import {
    checkModalValidationsForConfig, isValidEntry, PATTERNS
} from "../../../util/Validations";
import {ADTA_ASSET_TYPE, CATCH_ALL_INSTALLATION_TYPES, CONFIG_TYPE} from "../../../util/Constants";
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import Select, {SelectOption} from "@amzn/meridian/select";
import {getSorterConfigKey, getSorterConfigLocationStringForm, getInstallationTypes} from "../../../util/Util";
import {getLocalizedMessage} from "../../../util/Localization";
import {FEATURES, FeatureUtility} from "../../../util/FeatureUtility";

const AddAdtaSorterConfigurationModal = (props) => {

    const {formatMessage} = props.intl;
    const {mac, intl} = props;
    const {getDraftData, addInstance, editInstance} = useContext(DraftContext)
    const installationType = getInstallationTypes();

    // Visual controls
    const {open, setOpen} = props
    const [show, setShow] = useState(false)
    const [edit, setEdit] = useState(false)
    const [showFinger, setShowFinger] = useState(false)
    const [showLocation, setShowLocation] = useState(true)

    // Values
    const [locations, setLocations] = useState("")
    const [type, setType] = useState("")
    const [bayId, setBayId] = useState("")
    const [fingerName, setFingerName] = useState("")

    // Error
    const [locationsErr, setLocationsErr] = useState(false)
    const [typeErr, setTypeErr] = useState(false)
    const [bayIdErr, setBayIdErr] = useState(false)
    const [fingerNameErr, setFingerNameErr] = useState(false)

    const onCloseModal = () => {
        setShow(false);
        setOpen(false)
        resetInputState();
    }

    const modalSorterConfig = {
        bayId: bayId,
        fingerName: fingerName,
        installation: {
            type: type,
            locations: locations
        }
    }

    /* Filling a map: sorterConfigMap with bayId as key in case of an update of existing asset */
    const sorterConfigMap = {}
    getDraftData({stateKey: CONFIG_TYPE.SORTER_DESTINATION, subKey: mac}).forEach(value => {
        const key = getSorterConfigKey(value.bayId, value.installation.type);
        sorterConfigMap[key] = {
            locations: value.installation.locations,
            fingerName: value.fingerName,
            type: value.installation.type
        }
    });

    const onClickConfirm = (modalSorterConfig, mac) => {
        if (checkModalValidationsForConfig(modalSorterConfig, mac, ADTA_ASSET_TYPE)) {
            setConfigData(modalSorterConfig, mac);
            setOpen(false)
            resetInputState();
            setShow(false);
        } else {
            setShow(true)
        }
    }

    function resetInputState() {
        setBayId("");
        setBayIdErr(false);
        setType("");
        setTypeErr(false);
        resetConfigDetails();
        resetConfigErrorDetails();
    }

    const resetConfigDetails = () => {
        setLocations("");
        setFingerName("");
        setShowFinger(false);
        setShowLocation(true);
    }

    const resetConfigErrorDetails = () => {
        setTypeErr(false);
        setLocationsErr(false);
        setFingerNameErr(false);
    }

    const processType = (type) => {
        if (['miss', 'jackpot', 'recirculation', 'problem_solve'].includes(type)) {
            setShowFinger(true);
            setShowLocation(false);
        } else {
            setShowFinger(false);
            setShowLocation(true);
        }
    }

    const prefill = () => {
        const key = getSorterConfigKey(bayId, type);
        if (sorterConfigMap.hasOwnProperty(key)) {
            setEdit(true);
            resetConfigErrorDetails();
            setLocations(getSorterConfigLocationStringForm(sorterConfigMap[key].locations));
            setFingerName(sorterConfigMap[key].fingerName);
        } else if (edit) {
            setEdit(false);
            resetConfigDetails()
        }
    }

    const setConfigData = (modalSorterConfig, mac) => {
        if (CATCH_ALL_INSTALLATION_TYPES.includes(modalSorterConfig.installation.type)
            && modalSorterConfig.installation.locations.length === 0) {
            modalSorterConfig.installation.locations = [""];
        } else if (modalSorterConfig.installation.locations) {
            modalSorterConfig.installation.locations = modalSorterConfig.installation.locations
                .replace(/\s/g, '').split(';').filter(Boolean);
        } else {
            modalSorterConfig.installation.locations = [];
        }

        /* sorter config are stored and passed as map with bayId as key */
        if(edit) {
            editInstance({
                stateKey: CONFIG_TYPE.SORTER_DESTINATION,
                subKey: mac,
                instance: modalSorterConfig,
                keyComparator: (config1, config2) => (
                    getSorterConfigKey(config1.bayId, config1.installation.type) ===
                    getSorterConfigKey(config2.bayId, config2.installation.type)
                ),
                valueComparator: (config1, config2) => (
                    JSON.stringify(config1.fingerName) === JSON.stringify(config2.fingerName) &&
                    JSON.stringify(config1.installation.locations) === JSON.stringify(config2.installation.locations)
                )
            })
        } else {
            addInstance({
                stateKey: CONFIG_TYPE.SORTER_DESTINATION,
                subKey: mac,
                instance: modalSorterConfig
            })
        }

    }

    const generateHelperText = () => {
        return (CATCH_ALL_INSTALLATION_TYPES.includes(type) ?
            getLocalizedMessage({intl, stringId: "modal.optional"}) : "")
            + (FeatureUtility.getFeatureStatus(FEATURES.IS_SSD3_ENABLED) ?
                getLocalizedMessage({intl, stringId: "modal.adtr_location_example"})
                : getLocalizedMessage({intl, stringId: "modal.location_example"}))
    }

    return (
        <Row>
            <Modal
                title={formatMessage({id: "sorterconfig.addupdate"})}
                open={open}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Column spacing="200">
                        <b> <FormattedMessage id="modal.addheading"
                                              defaultMessage="To edit an existing configuration, enter the Bay Id."/>
                        </b>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            {getLocalizedMessage({intl, stringId: "destination.id"})}
                        </Text>
                        <Input id="bay-id-input"
                               value={bayId}
                               onChange={setBayId}
                               onBlur={() => {
                                   setBayIdErr(!isValidEntry(bayId, PATTERNS.BAY_ID))
                                   prefill();
                               }}
                               error={bayIdErr}/>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            <FormattedMessage id="modal.installationtype"
                                              defaultMessage="Installation Type"/>
                        </Text>
                        <Select id="type-input"
                            value={type}
                            onChange={setType}
                            onBlur={() => {
                                processType(type);
                                prefill();
                            }}
                            placeholder={<FormattedMessage id="modal.enter_value"
                                               defaultMessage="Enter value..."/>}
                            width="100%"
                            error={typeErr}>
                            {Object.values(installationType).map(({label, value}, index) =>
                                <SelectOption key={index} value={value} label={label} />
                            )}
                        </Select>
                    </Column>
                    { showLocation && <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            <FormattedMessage id="modal.location"
                                              defaultMessage="Locations"/>
                        </Text>
                        <Input id="location-input"
                               value={locations}
                               onChange={setLocations}
                               onBlur={() => {setLocationsErr(!(
                                   (CATCH_ALL_INSTALLATION_TYPES.includes(type)
                                       && isValidEntry(locations, PATTERNS.CATCH_ALL_LOCATION))
                                   || isValidEntry(locations, PATTERNS.LOCATION)
                               ))}}
                               error={locationsErr}
                               helperText={generateHelperText()}/>
                    </Column> }
                    { showFinger && <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            <FormattedMessage id="modal.fingerName"
                                              defaultMessage="Finger Name"/>
                        </Text>
                        <Input id="finger-name-input"
                               value={fingerName}
                               onChange={setFingerName}
                               onBlur={() => {setFingerNameErr(!isValidEntry(fingerName, PATTERNS.SHORT_STRING))}}
                               error={fingerNameErr}
                               helperText={<FormattedMessage id="modal.fingerName_desc"
                                         defaultMessage="[Optional field] These bays should be associated with their finger name to identify them in-case of multiple fingers within the same ADTA."/>}/>
                    </Column> }
                </Column>
                <ModalFooter>
                    <Column>
                        {!checkModalValidationsForConfig(modalSorterConfig, mac, ADTA_ASSET_TYPE) &&
                            show &&
                            <Alert type="warning"
                                   size="small">
                                <FormattedMessage id="alert.missingfields"
                                                  defaultMessage="Please enter all fields in correct format!"/>
                            </Alert>}
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onCloseModal}>
                            <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                        </Button>
                        <Button type="primary" size="medium"
                                onClick={() => onClickConfirm(modalSorterConfig, mac)}>
                            { edit ? <FormattedMessage id="modal.confirmUpdate" defaultMessage="Confirm update"/>
                                : <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )
}

export default injectIntl(AddAdtaSorterConfigurationModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/adtadevice/modal/AddAdtaSorterConfigurationModal.js